import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye } from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreatePurchaseOrderByQuotation } from "../../../../@types/services/CreatePurchaseOrderByQuotation";
import { CommonErrorHelp } from "../../../../components/molecules/CommonErrorHelp";
import {
  getCreatePurchaseOrderByQuotation,
  listCreatePurchaseOrderByQuotations,
} from "../../../../services/createPurchaseOrderByQuotations";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

type CreatePurchaseOrderByQuotationRowProps = {
  createPurchaseOrderByQuotationId: string;
};

const CreatePurchaseOrderByQuotationRow: React.FC<
  CreatePurchaseOrderByQuotationRowProps
> = ({ createPurchaseOrderByQuotationId }) => {
  const { t } = useTranslation();

  const [data, setData] = useState<CreatePurchaseOrderByQuotation>();
  const [loading, setLoading] = useState(true);

  const columnQuantity = useMemo(() => 4, []);

  const handleFetch = useCallback((id: string): void => {
    setLoading(() => true);
    getCreatePurchaseOrderByQuotation(id)
      .then((response) => {
        setData(() => response);
      })
      .finally(() => {
        setLoading(() => false);
      });
  }, []);

  useEffect(() => {
    handleFetch(createPurchaseOrderByQuotationId);
  }, [createPurchaseOrderByQuotationId, handleFetch]);

  if (loading) {
    return (
      <TableRow key={createPurchaseOrderByQuotationId}>
        {Array(columnQuantity).map(() => (
          <TableCell key={`skeleton-cell-${crypto.randomUUID()}`}>
            <Skeleton />
          </TableCell>
        ))}
      </TableRow>
    );
  }

  if (!data) {
    return (
      <TableRow key={createPurchaseOrderByQuotationId}>
        <TableCell colSpan={columnQuantity}>Fail to load</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow key={data.id}>
      <TableCell>
        {t("intlDateTime", i18nextParseToDate(data.created_at))}
      </TableCell>

      <TableCell>
        {t("intlDateTime", i18nextParseToDate(data.updated_at))}
      </TableCell>

      <TableCell align="center">
        {data.completed ? t("yes") : t("no")}
      </TableCell>

      <TableCell align="center">
        <Tooltip title={t("view")}>
          <IconButton component={Link} to={data.id}>
            <Icon icon={mdiEye} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

type CreatePurchaseOrderByQuotationTableProps = {
  completed: boolean;
};

const CreatePurchaseOrderByQuotationTable: React.FC<
  CreatePurchaseOrderByQuotationTableProps
> = ({ completed }) => {
  const { t } = useTranslation();

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createPurchaseOrderByQuotations, setCreatePurchaseOrderByQuotations] =
    useState<CreatePurchaseOrderByQuotation[]>([]);
  const [loading, setLoading] = useState(true);

  const handleFetch = useCallback(
    async (page: number) => {
      setLoading(() => true);
      await listCreatePurchaseOrderByQuotations({
        filterCompleted: completed,
        itemsPerPage: rowsPerPage,
        order: "desc",
        page,
      })
        .then((createPurchaseOrderByQuotation) => {
          setCreatePurchaseOrderByQuotations(
            () => createPurchaseOrderByQuotation.results || [],
          );
          setTotalRows(() => createPurchaseOrderByQuotation.total_items || 0);
        })
        .finally(() => {
          setLoading(() => false);
        });
    },
    [completed, rowsPerPage],
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    value: number,
  ): void => {
    setCurrentPage(() => value + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setRowsPerPage(() => parseInt(event.target.value, 10));
    setCurrentPage(() => 1);
  };

  useEffect(() => {
    handleFetch(currentPage);
  }, [currentPage, handleFetch]);

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell align="left">
              {t("createPurchaseOrderByQuotation.createdAt")}
            </TableCell>
            <TableCell align="left">
              {t("createPurchaseOrderByQuotation.updatedAt")}
            </TableCell>
            <TableCell align="center">
              {t("createPurchaseOrderByQuotation.completed")}
            </TableCell>
            <TableCell align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          {!!loading && (
            <TableRow key="loading-skeleton">
              {Array(4).map(() => (
                <TableCell key={`skeleton-cell-${crypto.randomUUID()}`}>
                  <Skeleton />
                </TableCell>
              ))}
            </TableRow>
          )}
          {!loading &&
            createPurchaseOrderByQuotations &&
            createPurchaseOrderByQuotations.map(
              (createPurchaseOrderByQuotation) => (
                <CreatePurchaseOrderByQuotationRow
                  createPurchaseOrderByQuotationId={
                    createPurchaseOrderByQuotation.id
                  }
                  key={createPurchaseOrderByQuotation.id}
                />
              ),
            )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              count={totalRows}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={currentPage - 1}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              showFirstButton
              showLastButton
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

const CreatePurchaseOrderByQuotations: React.FC = () => {
  const { t } = useTranslation();

  const [tabOpen, setTabOpen] = useState("notCompleted");

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ): void => {
    setTabOpen(() => newValue);
  };

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        justifyContent: "center",
        px: 2,
        width: "100%",
      }}
    >
      <h1>{t("createPurchaseOrderByQuotation.title")}</h1>

      <TabContext value={tabOpen}>
        <Box
          sx={{
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Tabs onChange={handleTabChange} value={tabOpen}>
            <Tab
              label={t("createPurchaseOrderByQuotation.notCompleted")}
              value="notCompleted"
            />
            <Tab
              label={t("createPurchaseOrderByQuotation.completed")}
              value="completed"
            />
          </Tabs>
          <CommonErrorHelp errorType="create_purchase_order_material_quotation" />
        </Box>

        <TabPanel value="notCompleted">
          <CreatePurchaseOrderByQuotationTable completed={false} />
        </TabPanel>

        <TabPanel value="completed">
          <CreatePurchaseOrderByQuotationTable completed />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/create_purchase_order_by_quotations/",
)({
  component: CreatePurchaseOrderByQuotations,
});
